/* eslint-disable react/jsx-key */
import React, { useCallback, useMemo, useState } from 'react';
import { Redirect, useParams, useHistory } from 'react-router';
import { Button, Col, Form, Row, Switch, Typography, message as messageAnt } from 'antd-v5';
import moment from 'moment';
import { connect } from 'react-redux';
import { useQuery } from 'react-query';
import StandardLayoutV2 from '../../../../../Layout/StandardLayoutV2';
import {
  APPOINTMENTS_MESSAGES,
  getAppointmentsConfig,
  updateAppointmentsConfig,
} from '../../../../../../services/appointments';
import TextAreaWithActions from '../../../../../../components/Layout/TextAreaWithActions';
import '../../../../../../components/Layout/chat/chat.scss';
import { FakeChatBalloon } from '../../../../../Layout/FakeChatBalloon';

const AppointmentsSettingsMessage = ({ storeName, associate }) => {
  const params = useParams();
  const { push } = useHistory();
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const message = Form.useWatch('messageTemplate', form);
  const { data: config, isFetching } = useQuery(
    ['appointmentConfig', params?.msg_id],
    async () => {
      try {
        const response = await getAppointmentsConfig();
        const configArray = APPOINTMENTS_MESSAGES.find(item => item.id === params?.msg_id);
        if (!configArray) return null;

        return {
          ...configArray,
          message: response[configArray.id]?.messageTemplate || configArray.defaultMessage || '',
          enabled: params?.msg_id === 'confirmation' ? true : response[configArray.id]?.enabled || false,
        };
      } catch (e) {
        console.error('Error fetching appointment config:', e);
        return null;
      }
    },
    {
      refetchOnWindowFocus: false,
      initialData: null,
    },
  );

  const description = useMemo(() => {
    switch (params?.msg_id) {
      case 'noShow':
        return 'This message is sent 1 day after an appointment status changes to “No Show”.';
      case 'reminder':
        return 'This message is sent 1 day before the scheduled appointment date as a reminder to the client.';
      case 'update':
        return 'This message is sent immediately when an appointment is updated.';
      case 'confirmation':
        return 'This message is sent immediately when a new appointment is created.';
      case 'canceled':
        return 'This message is sent immediately when an appointment is canceled.'
      default:
        return '';
    }
  }, [params?.msg_id]);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      const values = form.getFieldsValue();
      const enabled = params?.msg_id === 'confirmation' ? {} : { enabled: values.enabled };
      await updateAppointmentsConfig({
        [params?.msg_id]: {
          ...enabled,
          messageTemplate: values.messageTemplate,
        },
      });
      messageAnt.success('Appointment message saved successfully.');
    } catch (e) {
      console.error('Error saving appointment config:', e);
      messageAnt.error('Failed to save appointment message. Please try again.');
    } finally {
      setSaving(false);
      push('/Configuration/appointments');
    }
  }, [form, params?.msg_id, push]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    push('/Configuration/appointments');
  }, [form, push]);

  if (APPOINTMENTS_MESSAGES.findIndex(item => item.id === params?.msg_id) === -1) {
    return <Redirect to="/Configuration/appointments" />;
  }

  return (
    <StandardLayoutV2
      title={config ? `${config?.title}` : 'Appointment Message'}
      classProps="flex-col-left"
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent' }}
      loading={isFetching}
      breadcrumb={[
        { title: 'Setup' },
        { title: 'Appointments', path: '/Configuration/appointments' },
        { title: 'Edit Message' },
      ]}
      actions={
        <div className="flex-row items-center justify-end gap-8">
          <Button type="primary" onClick={handleSave} loading={saving} disabled={isFetching}>
            Save
          </Button>
          <Button onClick={handleCancel} disabled={isFetching || saving}>
            Cancel
          </Button>
        </div>
      }
    >
      <Row gutter={32} className="w-100-P">
        <Col span={12}>
          <Form
            form={form}
            layout="vertical"
            className="flex-col-left w-100-P gap-8"
            initialValues={{ enabled: config?.enabled, messageTemplate: config?.message }}
          >
            {params?.msg_id !== 'confirmation' && (
              <div className="flex-row items-ceter gap-8">
                <Form.Item name="enabled" valuePropName="checked" noStyle>
                  <Switch defaultChecked={config?.enabled} disabled={params?.msg_id === 'confirmation'} />
                </Form.Item>
                <span>Active</span>
              </div>
            )}
            <Typography.Text type="secondary">{description}</Typography.Text>
            <TextAreaWithActions
              appointments
              hideImages
              hideReviewLink
              containerStyleProps={{ border: '1px solid #d9d9d9', borderRadius: '6px', width: '100%' }}
              val={form.getFieldValue('messageTemplate')}
              changeHandler={value => form.setFieldValue('messageTemplate', value)}
              template
              associate=":associate:"
              hasResetTemplate
              resetTemplateMessage={config?.defaultMessage || ''}
            />
            <Form.Item name="messageTemplate" noStyle />
          </Form>
        </Col>
        <Col span={12}>
          <FakeChatBalloon message={message} associate={associate} storeName={storeName} />
        </Col>
      </Row>
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => {
  const storeName = state?.auth?.stores?.find(store => store.id === state.auth.selectedStore)?.name || '';

  return {
    storeName,
    associate: `${state?.auth?.firstName || ''} ${state?.auth?.lastName || ''}`.trim(),
  };
};
export default connect(mapStateToProps, {})(AppointmentsSettingsMessage);
